"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StepBody = exports.stepHoverStripBarClass = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const slate_react_1 = require("slate-react");
const slate_1 = require("slate");
const flexible_input_1 = require("flexible-input");
const use_t_1 = require("use-t");
const context_1 = require("@jsonjoy.com/ui/lib/styles/context");
const BlockPlaceholder_1 = require("../../components/blocks/BlockPlaceholder");
const StripBarHandle_1 = require("../../components/blocks/StripBarHandle");
const util_1 = require("../../util");
const css_1 = require("../../custom-style/css");
const contentColClass = (0, nano_theme_1.rule)({
    d: 'block',
    flex: '1 1 auto',
    minW: 0,
    pad: '1px 0 0 0',
});
const titleClass = (0, nano_theme_1.rule)({
    d: 'block',
    fw: 600,
    fz: '14px',
    lh: 1.35,
    mrb: '2px',
});
const descClass = (0, nano_theme_1.rule)({
    d: 'block',
    fz: '12px',
    lh: 1.45,
    mrb: '18px',
});
const stripSlotClass = (0, nano_theme_1.rule)({
    pos: 'relative',
    d: 'block',
    h: 0,
});
const stripOverlayClass = (0, nano_theme_1.rule)({
    pos: 'absolute',
    l: '0',
    t: '-8px',
    d: 'flex',
    fld: 'row',
    ai: 'center',
    gap: '8px',
    z: 1,
    pe: 'none',
});
const bodyClass = (0, nano_theme_1.rule)({
    pos: 'relative',
    d: 'block',
    minW: '0',
});
exports.stepHoverStripBarClass = (0, nano_theme_1.rule)({
    [`&:hover .${StripBarHandle_1.stripBarHandleTriggerClass}`]: {
        opacity: 1,
        pointerEvents: 'auto',
    },
    [`&:hover .${StripBarHandle_1.stripBarHandleTriggerClass} .${StripBarHandle_1.stripBarHandleFillClass}`]: {
        width: '100%',
    },
});
/**
 * Wire Enter/Tab and Escape for an inline metadata input inside a slate block.
 */
const bindVoidInputKeyDown = (el, editor, onCancel) => {
    if (!el)
        return () => { };
    const handler = (evt) => {
        const e = evt;
        e.stopPropagation();
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            e.target.blur();
            requestAnimationFrame(() => slate_react_1.ReactEditor.focus(editor));
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel?.();
            e.target.blur();
            requestAnimationFrame(() => slate_react_1.ReactEditor.focus(editor));
        }
    };
    el.addEventListener('keydown', handler);
    return () => el.removeEventListener('keydown', handler);
};
const useInlineField = (editor, element, field) => {
    const elementValue = (element[field] ?? '');
    const [editing, setEditing] = React.useState(false);
    const [draft, setDraft] = React.useState(elementValue);
    const focusedRef = React.useRef(false);
    React.useEffect(() => {
        if (!focusedRef.current)
            setDraft(elementValue);
    }, [elementValue]);
    const commit = React.useCallback(() => {
        focusedRef.current = false;
        const next = draft.trim();
        try {
            const path = slate_react_1.ReactEditor.findPath(editor, element);
            if (next) {
                slate_1.Transforms.setNodes(editor, { [field]: next }, { at: path });
            }
            else {
                slate_1.Transforms.unsetNodes(editor, field, { at: path });
            }
        }
        catch { }
        if (!next)
            setEditing(false);
    }, [editor, element, draft, field]);
    return { editing, setEditing, draft, setDraft, focusedRef, commit };
};
const StepBody = ({ element, state, children }) => {
    const editor = (0, slate_react_1.useSlateStatic)();
    const readOnly = (0, slate_react_1.useReadOnly)();
    const styles = (0, context_1.useStyles)();
    const [t] = (0, use_t_1.useT)();
    const titleField = useInlineField(editor, element, 'stepTitle');
    const descField = useInlineField(editor, element, 'stepDesc');
    const [titleInputEl, setTitleInputEl] = React.useState(null);
    React.useEffect(() => {
        if (readOnly)
            return;
        return bindVoidInputKeyDown(titleInputEl, editor, () => {
            titleField.setDraft(element.stepTitle ?? '');
            titleField.focusedRef.current = false;
            if (!element.stepTitle)
                titleField.setEditing(false);
        });
    }, [titleInputEl, readOnly, element.stepTitle, editor, titleField]);
    const [descInputEl, setDescInputEl] = React.useState(null);
    React.useEffect(() => {
        if (readOnly)
            return;
        return bindVoidInputKeyDown(descInputEl, editor, () => {
            descField.setDraft(element.stepDesc ?? '');
            descField.focusedRef.current = false;
            if (!element.stepDesc)
                descField.setEditing(false);
        });
    }, [descInputEl, readOnly, element.stepDesc, editor, descField]);
    const hasTitle = !!element.stepTitle;
    const hasDesc = !!element.stepDesc;
    const showTitleField = hasTitle || titleField.editing;
    const showDescField = hasDesc || descField.editing;
    const showTitleBar = !readOnly && !showTitleField;
    const showDescBar = !readOnly && !showDescField;
    const titleColor = state === 'pending' ? (0, css_1.fgVar)(35, styles.g(0.35)) : 'inherit';
    const descColor = (0, css_1.fgVar)(45, styles.g(0.45));
    const renderBar = (kind) => {
        const label = kind === 'title' ? t('Add title') : t('Add description');
        return (React.createElement(StripBarHandle_1.StripBarHandle, { tooltip: label, ariaLabel: label, onActivate: () => {
                if (kind === 'title')
                    titleField.setEditing(true);
                else
                    descField.setEditing(true);
            } }));
    };
    // Top strip slot hosts title bar (if needed) and the description bar when
    // there's no title field above the description. Mid slot hosts the
    // description bar when the title field IS shown, so it sits between title
    // and body.
    const topSlotHasTitle = showTitleBar;
    const topSlotHasDesc = showDescBar && !showTitleField;
    const midSlotHasDesc = showDescBar && showTitleField;
    const showTopSlot = topSlotHasTitle || topSlotHasDesc;
    return (React.createElement("span", { className: contentColClass },
        showTopSlot && (React.createElement("span", { className: stripSlotClass, contentEditable: false },
            React.createElement("span", { className: stripOverlayClass },
                topSlotHasTitle && renderBar('title'),
                topSlotHasDesc && renderBar('desc')))),
        showTitleField ? (React.createElement("span", { className: titleClass, contentEditable: false, style: { color: titleColor } }, readOnly ? (element.stepTitle) : (React.createElement(flexible_input_1.FlexibleInput, { inp: setTitleInputEl, value: titleField.draft, minWidth: 60, focus: titleField.editing && !hasTitle, typeahead: titleField.draft ? '' : t('Add title…'), onChange: (e) => titleField.setDraft(e.target.value), onFocus: () => {
                titleField.focusedRef.current = true;
            }, onBlur: titleField.commit })))) : null,
        midSlotHasDesc && (React.createElement("span", { className: stripSlotClass, contentEditable: false },
            React.createElement("span", { className: stripOverlayClass, style: { top: '-6px' } }, renderBar('desc')))),
        showDescField ? (React.createElement("span", { className: descClass, contentEditable: false, style: { color: descColor } }, readOnly ? (element.stepDesc) : (React.createElement(flexible_input_1.FlexibleInput, { inp: setDescInputEl, value: descField.draft, minWidth: 120, focus: descField.editing && !hasDesc, typeahead: descField.draft ? '' : t('Add description…'), onChange: (e) => descField.setDraft(e.target.value), onFocus: () => {
                descField.focusedRef.current = true;
            }, onBlur: descField.commit })))) : null,
        React.createElement("span", { className: bodyClass, style: { opacity: state === 'done' ? 0.72 : 1 } },
            children,
            (0, util_1.isEmptyBlock)(element) && React.createElement(BlockPlaceholder_1.BlockPlaceholder, { element: element }))));
};
exports.StepBody = StepBody;
